import {
  Stack,
  Avatar,
  TableRow,
  MenuItem,
  TableCell,
  IconButton,
  Typography,
  ListItemText,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import Label from 'src/components/label';

import { TransactionStatus } from 'src/type/transaction';
import { fNumber } from 'src/utils/format-number';

import { fDate, fTime } from 'src/utils/format-time';
import { useBoolean } from 'src/hooks/use-boolean';
import { TopupTxType } from 'src/type/topup-deposit';
import { shortString } from 'src/utils/format-string';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { txStatusLabel } from './utils';
import TopupViewDialog from './topup-view-dialog';
import TopupRejectDialog from './topup-reject-dialog';
import TopupApproveDialog from './topup-approve-dialog';

type Props = {
  row: TopupTxType;
  handleReload: () => void;
};

export default function TopupTableRow({ row, handleReload }: Props) {
  const popover = usePopover();
  const approve = useBoolean();
  const reject = useBoolean();
  const view = useBoolean();

  const { id, createdAt, creditAmount, status, updatedAt, user } = row;

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack direction="row">
            <Avatar alt="" sx={{ mr: 2 }}>
              {user?.firstName?.[0].toUpperCase()}
            </Avatar>

            <ListItemText
              disableTypography
              primary={
                <Typography variant="body2" noWrap>
                  {user?.firstName} {user?.lastName}
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                  #{shortString(id)}
                </Typography>
              }
            />
          </Stack>
        </TableCell>

        <TableCell>{fNumber(creditAmount || 0)} THB</TableCell>

        <TableCell>
          <ListItemText
            primary={fDate(createdAt, 'DD MMMM BBBB')}
            secondary={fTime(createdAt)}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell>
          {status === TransactionStatus.SUCCESS ? (
            <ListItemText
              primary={fDate(updatedAt, 'DD MMMM BBBB')}
              secondary={fTime(updatedAt)}
              primaryTypographyProps={{ typography: 'body2', noWrap: true }}
              secondaryTypographyProps={{
                mt: 0.5,
                component: 'span',
                typography: 'caption',
              }}
            />
          ) : (
            '-'
          )}
        </TableCell>

        <TableCell>
          <Label
            variant="soft"
            color={
              (status === TransactionStatus.SUCCESS && 'success') ||
              (status === TransactionStatus.PENDING && 'warning') ||
              (status === TransactionStatus.REJECTED && 'error') ||
              (status === TransactionStatus.CANCEL && 'info') ||
              'default'
            }
          >
            {txStatusLabel(status)}
          </Label>
        </TableCell>

        <TableCell align="right" sx={{ px: 2 }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="top-right"
        sx={{ width: 160 }}
      >
        {status === TransactionStatus.PENDING && (
          <>
            <MenuItem
              onClick={() => {
                popover.onClose();
                approve.onTrue();
              }}
              sx={{ color: 'success.main' }}
            >
              <Iconify icon="solar:check-square-bold" />
              อนุมัติ
            </MenuItem>
            <MenuItem
              onClick={() => {
                popover.onClose();
                reject.onTrue();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:close-square-bold" />
              ปฏิเสธ
            </MenuItem>
          </>
        )}

        <MenuItem
          onClick={() => {
            popover.onClose();
            view.onTrue();
          }}
        >
          <Iconify icon="solar:info-square-bold" />
          รายละเอียด
        </MenuItem>
      </CustomPopover>

      {approve.value && (
        <TopupApproveDialog
          open={approve.value}
          onClose={approve.onFalse}
          handleReload={handleReload}
          tx={row}
        />
      )}

      {reject.value && (
        <TopupRejectDialog
          open={reject.value}
          onClose={reject.onFalse}
          handleReload={handleReload}
          tx={row}
        />
      )}

      {view.value && <TopupViewDialog open={view.value} onClose={view.onFalse} tx={row} />}
    </>
  );
}
